import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Lock, Crown, Sparkles } from 'lucide-react'
import { useSubscriptionStore } from '../stores/subscriptionStore'
import SubscriptionPlans from './SubscriptionPlans'

interface SubscriptionGuardProps {
  children: React.ReactNode
  feature?: string
  showUpgradePrompt?: boolean
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({ 
  children, 
  feature = 'this feature',
  showUpgradePrompt = true 
}) => {
  const { isSubscriptionActive } = useSubscriptionStore()
  const [showPlans, setShowPlans] = useState(false)

  if (isSubscriptionActive()) {
    return <>{children}</>
  }

  if (!showUpgradePrompt) {
    return null
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative"
      >
        {/* Blurred Content */}
        <div className="blur-sm pointer-events-none">
          {children}
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-2xl flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center mx-auto mb-6">
              <img 
                src="/rraasi_transparent.png" 
                alt="Rraasi Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            
            <h3 className="text-2xl font-bold text-white mb-4">
              Premium Feature
            </h3>
            
            <p className="text-gray-300 mb-6 max-w-md">
              {feature} is available exclusively for our premium subscribers. 
              Upgrade now to unlock unlimited access to all cosmic insights.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPlans(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              >
                <Crown className="w-5 h-5" />
                Upgrade Now
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPlans(false)}
                className="bg-gray-700 text-gray-300 px-8 py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
              >
                Maybe Later
              </motion.button>
            </div>

            {/* Features Preview */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-gray-800/50 rounded-xl p-4">
                <Sparkles className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <h4 className="text-white font-semibold mb-1">Unlimited Readings</h4>
                <p className="text-gray-400 text-sm">Get unlimited astrology readings</p>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-4">
                <Crown className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <h4 className="text-white font-semibold mb-1">Premium Features</h4>
                <p className="text-gray-400 text-sm">Access advanced cosmic insights</p>
              </div>
              
              <div className="bg-gray-800/50 rounded-xl p-4">
                <Lock className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <h4 className="text-white font-semibold mb-1">Priority Support</h4>
                <p className="text-gray-400 text-sm">Get priority customer support</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Subscription Plans Modal */}
      {showPlans && (
        <SubscriptionPlans 
          onClose={() => setShowPlans(false)}
          showUpgradePrompt={true}
        />
      )}
    </>
  )
}

export default SubscriptionGuard 